import type { BotPersonality, SessionKind, SkinDefinition } from "./domain";

export interface YakTrailsConfig {
  configVersion: string;
  rulesetVersion: string;
  world: {
    radius: number;
    gridSize: number;
    capacity: number;
    startingTerritoryRadius: number;
    spawnSafetyRadius: number;
    defaultBotFillTarget: number;
  };
  mechanics: {
    simulationHz: number;
    movementSpeed: number;
    maximumTurnRadiansPerSecond: number;
    trailSampleDistance: number;
    trailCollisionRadius: number;
    inputDeadZone: number;
    respawnDelayMs: number;
    botThinkIntervalTicks: number;
    boundaryInset: number;
    spawnSearchAttempts: number;
    minimumTrailPointDistance: number;
    captureBarrierSampleCellFraction: number;
    fullCapturePercent: number;
  };
  bots: {
    personality: Record<BotPersonality, {
      aggression: number;
      maximumTrailSamples: number;
    }>;
    attackSearchRadius: number;
    wanderAngleRadians: number;
    wanderDistance: number;
    boundaryRecoveryRadiusFraction: number;
  };
  network: {
    snapshotHz: number;
    reconnectGraceMs: number;
    reconnectTokenLifetimeMs: number;
    maximumFrameBytes: number;
    maximumCommandsPerSecond: number;
    slowClientBufferedBytes: number;
    checkpointIntervalTicks: number;
    presenceRenewalIntervalTicks: number;
    presenceLeaseMs: number;
    reconnectTicketPaddingMs: number;
    roomStatsSampleCount: number;
  };
  population: {
    maximumLivesBySession: Record<SessionKind, number>;
    minimumNeutralPercentForRespawn: number;
    neutralPercentPerActivePlayer: number;
  };
  teams: {
    redColor: string;
    blueColor: string;
    defaultLivesPerPlayer: number;
    suggestedLivesPerPlayer: number[];
    minimumLivesPerPlayer: number;
    maximumLivesPerPlayer: number;
    allowUnlimitedLives: boolean;
    territoryVictoryPercent: number;
    homeSideSpawnOffset: number;
    homeSideInset: number;
    minimumRespawnTerritoryCoverage: number;
    preserveTerritoryWhileRespawnEligible: boolean;
    minimumParticipants: number;
    minimumTeamsToStart: number;
    countdownMs: number;
  };
  tournament: {
    maximumStages: number;
    maximumEntrants: number;
    maximumCheckInPartitions: number;
    entrantsPerPartition: number;
    publicCatalogCacheTtlMs: number;
    standingsCacheTtlMs: number;
    defaultCheckInWindowMs: number;
    defaultRespawnDelayMs: number;
    maximumOverrideEntrantsPerRequest: number;
    maximumPublicBracketHeatsPerStage: number;
  };
  progression: {
    maximumXpPerRun: number;
    survivalSecondsPerXp: number;
    peakTerritoryMultiplier: number;
    xpPerElimination: number;
    fullCaptureBonus: number;
  };
  skins: SkinDefinition[];
}

export const YAK_TRAILS_SKINS: SkinDefinition[] = [
  { id: "classic", name: "Classic Cube", kind: "cube", unlockXp: 0, starter: true, primaryColor: "#ffca3a", secondaryColor: "#f59e0b", accentColor: "#fff7d6" },
  { id: "yak", name: "Yak", kind: "animal", unlockXp: 0, starter: true, primaryColor: "#7c4a2d", secondaryColor: "#f4ead9", accentColor: "#ef4444" },
  { id: "duck", name: "Duck", kind: "animal", unlockXp: 0, starter: true, primaryColor: "#ffd60a", secondaryColor: "#ff9f1c", accentColor: "#fff7b2" },
  { id: "cat", name: "Cat", kind: "animal", unlockXp: 100, starter: false, primaryColor: "#ff9f68", secondaryColor: "#fff0db", accentColor: "#3d405b" },
  { id: "pig", name: "Pig", kind: "animal", unlockXp: 250, starter: false, primaryColor: "#ff8fab", secondaryColor: "#ffc2d1", accentColor: "#c9184a" },
  { id: "frog", name: "Frog", kind: "animal", unlockXp: 500, starter: false, primaryColor: "#70e000", secondaryColor: "#ccff33", accentColor: "#1b4332" },
  { id: "corgi", name: "Corgi", kind: "animal", unlockXp: 900, starter: false, primaryColor: "#e98b2a", secondaryColor: "#fff3d6", accentColor: "#3f2a1d" },
  { id: "panda", name: "Panda", kind: "animal", unlockXp: 1400, starter: false, primaryColor: "#f8fafc", secondaryColor: "#111827", accentColor: "#f472b6" },
  { id: "burger", name: "Burger", kind: "food", unlockXp: 2100, starter: false, primaryColor: "#f6bd60", secondaryColor: "#6a994e", accentColor: "#bc4749" },
  { id: "sushi", name: "Sushi", kind: "food", unlockXp: 3000, starter: false, primaryColor: "#f8f9fa", secondaryColor: "#1f2937", accentColor: "#fb7185" },
  { id: "donut", name: "Donut", kind: "food", unlockXp: 4200, starter: false, primaryColor: "#d99b73", secondaryColor: "#f472b6", accentColor: "#67e8f9" },
  { id: "unicorn", name: "Unicorn", kind: "fantasy", unlockXp: 5500, starter: false, primaryColor: "#fdf4ff", secondaryColor: "#c084fc", accentColor: "#22d3ee" },
];

export const DEFAULT_YAK_TRAILS_CONFIG: YakTrailsConfig = {
  configVersion: "yak-trails-2026-08-10.2",
  rulesetVersion: "yak-trails-v3",
  world: {
    radius: 50,
    gridSize: 160,
    capacity: 12,
    startingTerritoryRadius: 5,
    spawnSafetyRadius: 11,
    defaultBotFillTarget: 8,
  },
  mechanics: {
    simulationHz: 20,
    movementSpeed: 8,
    maximumTurnRadiansPerSecond: Math.PI * 2,
    trailSampleDistance: 0.5,
    trailCollisionRadius: 0.72,
    inputDeadZone: 0.08,
    respawnDelayMs: 1500,
    botThinkIntervalTicks: 8,
    boundaryInset: 0.75,
    spawnSearchAttempts: 64,
    minimumTrailPointDistance: 0.01,
    captureBarrierSampleCellFraction: 0.35,
    fullCapturePercent: 99.999,
  },
  bots: {
    personality: {
      cautious: { aggression: 0.22, maximumTrailSamples: 11 },
      balanced: { aggression: 0.5, maximumTrailSamples: 17 },
      aggressive: { aggression: 0.8, maximumTrailSamples: 24 },
    },
    attackSearchRadius: 18,
    wanderAngleRadians: 0.9,
    wanderDistance: 12,
    boundaryRecoveryRadiusFraction: 0.78,
  },
  network: {
    snapshotHz: 10,
    reconnectGraceMs: 20_000,
    reconnectTokenLifetimeMs: 6 * 60 * 60 * 1_000,
    maximumFrameBytes: 256 * 1024,
    maximumCommandsPerSecond: 30,
    slowClientBufferedBytes: 512 * 1024,
    checkpointIntervalTicks: 40,
    presenceRenewalIntervalTicks: 400,
    presenceLeaseMs: 60_000,
    reconnectTicketPaddingMs: 5_000,
    roomStatsSampleCount: 240,
  },
  population: {
    maximumLivesBySession: {
      practice: 2,
      ranked_solo: 1,
      quick: 2,
      party: 4,
      tournament: 1,
    },
    minimumNeutralPercentForRespawn: 18,
    neutralPercentPerActivePlayer: 7,
  },
  teams: {
    redColor: "#ff4f64",
    blueColor: "#3d8dff",
    defaultLivesPerPlayer: 3,
    suggestedLivesPerPlayer: [1, 3, 5],
    minimumLivesPerPlayer: 1,
    maximumLivesPerPlayer: 9,
    allowUnlimitedLives: true,
    territoryVictoryPercent: 75,
    homeSideSpawnOffset: 22,
    homeSideInset: 2,
    minimumRespawnTerritoryCoverage: 0.6,
    preserveTerritoryWhileRespawnEligible: true,
    minimumParticipants: 2,
    minimumTeamsToStart: 2,
    countdownMs: 3_000,
  },
  tournament: {
    maximumStages: 16,
    maximumEntrants: 25_000,
    maximumCheckInPartitions: 64,
    entrantsPerPartition: 512,
    publicCatalogCacheTtlMs: 15_000,
    standingsCacheTtlMs: 5_000,
    defaultCheckInWindowMs: 30 * 60 * 1_000,
    defaultRespawnDelayMs: 2_500,
    maximumOverrideEntrantsPerRequest: 100,
    maximumPublicBracketHeatsPerStage: 128,
  },
  progression: {
    maximumXpPerRun: 250,
    survivalSecondsPerXp: 5,
    peakTerritoryMultiplier: 2,
    xpPerElimination: 20,
    fullCaptureBonus: 100,
  },
  skins: YAK_TRAILS_SKINS,
};

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, nested]) => `${JSON.stringify(key)}:${stableStringify(nested)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

export function hashYakTrailsConfig(config: YakTrailsConfig): string {
  const input = stableStringify(config);
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

export const DEFAULT_YAK_TRAILS_CONFIG_HASH = hashYakTrailsConfig(DEFAULT_YAK_TRAILS_CONFIG);
